"use strict";
const EventEmitter = require("events");
const _ = require("lodash");

const Renderer = require("./AdvancedFIlterRenderer");
const DateTimePickerInput = require("./date_time_picker_input/DateTimePickerInput");

class AdvancedFilterComponent extends EventEmitter {
    constructor(options) {
        super();
        this._mongoQuery = _.get(options, "filterData");

        this._durationTypeFields = new Set();
        // Ordering the columns alphabetically
        this._columnsConfig = _.get(options, "columnsConfig", []).sort((a, b) => {
            // Add the duration column names to an array for easy checking later
            const addToDurationFields = (column) => {
                if (column.dataType === "duration") {
                    this._durationTypeFields.add(column.name);
                }
            };

            addToDurationFields(a);
            addToDurationFields(b);

            if (a.label < b.label) {
                return -1;
            }
            return 1;
        });

        this._type = _.get(options, "type", "");

        this._allowGroups = _.get(options, "allowGroups", true);

        this._renderer = new Renderer(this, {
            filterData: this._mongoQuery,
            fieldsConfig: this._getFilterFieldsConfig(this._columnsConfig),
            allowGroups: this._allowGroups,
        });

        this._generatedInputs = {};
        this._mongoQueryChanged = this._mongoQueryChanged.bind(this);
        this._closeAdvancedFilter = this._closeAdvancedFilter.bind(this);
        this.unregisterInput = this.unregisterInput.bind(this);
        this._registerListeners();

        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this.renderer.on("AdvancedFilterUserChanged", this._mongoQueryChanged);
        this.renderer.on("AdvancedFilterUserClose", this._closeAdvancedFilter);
        this.renderer.on("UnregisterInput", this.unregisterInput);
    }

    _unregisterListeners() {
        this.renderer.removeListener("AdvancedFilterUserChanged", this._mongoQueryChanged);
        this.renderer.removeListener("AdvancedFilterUserClose", this._closeAdvancedFilter);
        this.renderer.removeListener("UnregisterInput", this.unregisterInput);
    }

    _mongoQueryChanged(mongoQuery) {
        this._mongoQuery = mongoQuery;
        this.emit("MongoQueryChanged", mongoQuery);
    }

    _closeAdvancedFilter() {
        this.emit("AdvancedFilterClose");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    getRootElement() {
        return this.renderer.getRootElement();
    }

    generateCalendarInput(fieldName) {
        const registerInput = this.registerInput.bind(this);
        const getInput = this.getInput.bind(this);

        return {
            input: function createDatepicker(rule, name) {
                const builder = this; //this is in queryBuilder context
                const newDTPInstance = new DateTimePickerInput(fieldName);
                newDTPInstance.setInputName(name);
                registerInput(rule.id, newDTPInstance);
                newDTPInstance.on("ValueChanged", () => {
                    if (!rule._updating_input) {
                        rule._updating_value = true;
                        rule.value = builder.getRuleInputValue(rule);
                        rule._updating_value = false;
                    }
                });
                newDTPInstance.on("CalendarToggled", (state, height) => {
                    const datetimeRoot = newDTPInstance.rootElement;
                    const datetimeCalendar = newDTPInstance.calendarPopup;
                    const datetimeBoundaries = datetimeRoot.getBoundingClientRect();
                    const windowHeight = window.innerHeight;

                    if (windowHeight - state * (datetimeBoundaries.top + height) < 0) {
                        datetimeCalendar.style.bottom =
                            windowHeight - datetimeBoundaries.bottom + "px";
                    } else {
                        datetimeCalendar.style.bottom = null;
                    }
                });

                return newDTPInstance.rootElement;
            },
            valueGetter: (rule) => {
                const input = getInput(rule.id);
                return input ? input.getValue() : "";
            },
            valueSetter: (rule, value) => {
                const input = getInput(rule.id);
                return input && input.setValue(value);
            },
        };
    }

    shutdown() {
        this._unregisterListeners();
        const generatedInputsShutdown = _.invokeMap(this._generatedInputs, "shutdown");

        return Promise.all(generatedInputsShutdown)
            .then(() => this._renderer.shutdown())
            .then(
                () => {},
                (err) => {
                    console.warn("AdvancedFilter.shutdown error", err);
                }
            );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    getInput(name) {
        return this._generatedInputs[name];
    }
    registerInput(name, input) {
        this._generatedInputs[name] = input;
    }

    unregisterInput(name) {
        const input = this.getInput(name);

        Promise.resolve()
            .then((res) => (input ? input.shutdown() : res))
            .then(() => {
                this._generatedInputs = _.omit(this._generatedInputs, name);
            });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getFilterFieldsConfig(columnsConfig) {
        const getDataType = (dataType) => {
            const allowedTypes = [
                "string",
                "integer",
                "double",
                "date",
                "time",
                "datetime",
                "boolean",
                "duration",
            ];
            if (allowedTypes.includes(dataType)) {
                return dataType;
            }
            switch (dataType) {
                case "number":
                    return "integer";
                default:
                    return "string";
            }
        };

        // Option keys are always strings, so a select over a numeric or boolean field needs the
        // value's own type here. queryBuilder casts the selected value with it before querying.
        const getSelectValueType = (dataType, valueDataType) => {
            if (dataType !== "enum" && dataType !== "array") {
                return undefined;
            }
            switch (valueDataType) {
                case "number":
                case "boolean":
                    return getDataType(valueDataType);
                default:
                    return undefined;
            }
        };

        const generateFilterItem = ({
            name,
            label,
            dataType,
            valueDataType,
            options,
            validation,
            typeInfo,
            forceSelect,
        }) => {
            const normalisedDataType =
                getSelectValueType(dataType, valueDataType) || getDataType(dataType);

            const filterItem = {
                id: name,
                field: name,
                label: label,
                type: normalisedDataType,
                size: 35,
            };

            if (dataType === "enum" || dataType === "array") {
                if (typeInfo && !_.isEmpty(typeInfo.ids) && !typeInfo.multiSelect) {
                    // Custom fields store IDs (UUIDs) as values — use typeInfo to build options
                    const {ids = [], labels = []} = typeInfo;
                    options = ids.map((id, idx) => ({[id]: labels[idx] || id}));
                } else if (!_.isEmpty(options)) {
                    // Normalize object-shaped options ({key: displayText} or {key: {value, label}}) to array form
                    if (_.isPlainObject(options)) {
                        options = _.map(options, (val, key) =>
                            _.isPlainObject(val) ? val : {value: key, label: val}
                        );
                    } else if (!_.isArray(options)) {
                        options = [];
                    }
                    // EDP/device fields: convert [{value, label}] to queryBuilder format [{dbValue: displayText}].
                    // Handle any property naming: value/name for DB key, label/title for display.
                    options = options
                        .map((opt) => {
                            const val = opt.value !== undefined ? opt.value : opt.name;
                            const display = opt.label || opt.title || val;
                            if (val === undefined && display === undefined) {
                                return null;
                            }
                            return {[val]: display};
                        })
                        .filter(Boolean);
                }
            }

            if (!_.isEmpty(validation)) {
                Object.assign(filterItem, {validation: validation});
            }

            switch (dataType) {
                case "enum":
                    if (!_.isEmpty(options)) {
                        Object.assign(filterItem, {
                            values: _.sortBy(options, (opt) => Object.values(opt)[0]),
                            input: "select",
                            operators: ["equal", "not_equal", "is_empty", "is_not_empty"],
                        });
                    }
                    break;
                case "longstring":
                    Object.assign(filterItem, {
                        values: options,
                        input: "text",
                        operators: [
                            "longstring_begins_with",
                            "longstring_not_begins_with",
                            "contains",
                            "string_contains_regex",
                            "not_contains",
                            "string_doesnt_contains_regex",
                            "longstring_ends_with",
                            "longstring_not_ends_with",
                            "is_empty",
                            "is_not_empty",
                        ],
                    });
                    break;
                case "duration":
                    Object.assign(filterItem, {
                        values: options,
                        input: "number",
                        operators: [
                            "equal",
                            "not_equal",
                            "less",
                            "less_or_equal",
                            "greater",
                            "greater_or_equal",
                            "between",
                            "not_between",
                            "is_empty",
                            "is_not_empty",
                        ],
                    });
                    break;
                case "pathlist":
                    Object.assign(filterItem, {
                        values: options,
                        input: "select",
                        operators: ["pathlist_equals", "pathlist_not_equals"],
                    });
                    break;
                case "array":
                    // forceSelect keeps id-based fields (e.g. Tags) a select even with no
                    // options, instead of degrading to a text box expecting a raw id.
                    if (!_.isEmpty(options) || forceSelect) {
                        Object.assign(filterItem, {
                            values: _.sortBy(options, (opt) => Object.values(opt)[0]),
                            input: "select",
                            operators: [
                                "array_includes",
                                "array_not_includes",
                                "is_empty",
                                "is_not_empty",
                            ],
                        });
                    } else {
                        Object.assign(filterItem, {
                            values: options,
                            input: "text",
                            operators: [
                                "equal",
                                "string_equals_regex",
                                "not_equal",
                                "string_not_equals_regex",
                                "begins_with",
                                "not_begins_with",
                                "contains",
                                "string_contains_regex",
                                "not_contains",
                                "string_doesnt_contains_regex",
                                "ends_with",
                                "not_ends_with",
                                "is_empty",
                                "is_not_empty",
                            ],
                        });
                    }
                    break;
                case "string":
                    if (!_.isEmpty(this._type)) {
                        Object.assign(filterItem, {
                            values: options,
                            input: "text",
                            operators: [
                                "must_contain_regex",
                                "must_not_contain_regex",
                                "must_not_contain_ordered_regex",
                                "must_only_contain_regex",
                                "must_contain_ordered_regex",
                                "must_only_contain_ordered_regex",
                                "must_contain",
                                "must_not_contain",
                                "must_not_contain_ordered",
                                "must_only_contain",
                                "must_contain_ordered",
                                "must_only_contain_ordered",
                            ],
                        });
                    }
                    break;
                case "boolean":
                    Object.assign(filterItem, {
                        operators: ["equal", "is_empty", "is_not_empty"],
                        input: "radio",
                        default_value: true,
                        values: {
                            1: "Yes",
                            0: "No",
                        },
                        valueSetter: function (rule, value) {
                            value = Number(value) || 0;
                            const name = rule.id + "_value_0";
                            rule.$el
                                .find("[name=" + name + '][value="' + value + '"]')
                                .prop("checked", true)
                                .trigger("change");
                        },
                    });
                    break;
                case "date":
                    Object.assign(
                        filterItem,
                        {
                            operators: [
                                "equal",
                                "less",
                                "less_or_equal",
                                "greater",
                                "greater_or_equal",
                                "is_empty",
                                "is_not_empty",
                            ],
                        },
                        this.generateCalendarInput(name)
                    );
                    break;
            }

            return filterItem;
        };

        return columnsConfig.map(generateFilterItem);
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    setQuery(query) {
        this.emit("SetQuery", query);
    }

    updateHeight(height) {
        this.emit("UpdateHeight", height);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}
module.exports = AdvancedFilterComponent;
